import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Music4, Radio, Tv, Briefcase, Sparkles, Lightbulb } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { assetUrl } from "@/lib/api";
import { Eyebrow, FadeUp, MaskedLines } from "@/components/Reveal";

export default function Events() {
  const { settings } = useSettings();
  const images = settings?.images || {};
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const gallery = [
    { key: "concert", icon: Music4, title: "Concerts & Festivals", cap: "Stage, sound, lighting and artists — we got it all." },
    { key: "stage", icon: Lightbulb, title: "Stage & Lighting", cap: "Show-grade rigs designed and operated by pros." },
    { key: "broadcast", icon: Radio, title: "Live Radio Shows", cap: "Advertise your business and events on live radio." },
    { key: "service-radiotv", icon: Tv, title: "Live TV", cap: "Take your project and ideas to Live TV — coordinated end to end." },
    { key: "team", icon: Briefcase, title: "Corporate Events", cap: "Corporate or personal — we take it to the next level." },
    { key: "office", icon: Sparkles, title: "Launches & Showcases", cap: "Product launches that get the attention you need." },
  ];

  const specs = ["Stage", "Sound", "Lighting", "Artists", "Broadcast", "Coordination"];

  return (
    <div data-testid="events-page">
      <section ref={heroRef} className="relative flex min-h-[70vh] items-end overflow-hidden" data-testid="events-hero">
        <motion.img src={assetUrl(images.concert)} alt="" aria-hidden style={{ y }} className="absolute inset-0 h-[115%] w-full object-cover" />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
        <div className="relative mx-auto w-full max-w-7xl px-4 pb-16 pt-32 sm:px-6 lg:px-12 lg:pb-24">
          <Eyebrow>Events · Concerts · Broadcast</Eyebrow>
          <h1 className="mt-6 font-display text-5xl font-extrabold tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="events-title">
            <MaskedLines baseDelay={0.2} lines={[<>We rock</>, <span className="bg-gradient-to-r from-cyan-300 to-indigo-400 bg-clip-text text-transparent">the show.</span>]} />
          </h1>
          <p className="mt-6 max-w-xl text-base text-slate-300 sm:text-lg" data-testid="events-subline">
            Hosting a corporate or personal event? Planning a live concert? We assist in taking it to the next level — and we make sure your project is noticed.
          </p>
          <Link to="/contact" data-testid="events-hero-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-7 py-3.5 text-sm font-semibold text-ink transition hover:bg-cyan-300">
            Book your event <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-12 lg:py-24" data-testid="events-gallery">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>What we produce</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">From boardrooms to stadiums</h2>
          </div>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {gallery.map((g, i) => {
            const Icon = g.icon;
            const src = assetUrl(images[g.key] || images.concert);
            return (
              <FadeUp key={g.key} delay={0.05 * i} className="h-full">
                <div className="group relative h-full overflow-hidden rounded-2xl border border-white/10" data-testid={`event-card-${g.key}`}>
                  <img src={src} alt={g.title} className="aspect-[4/3] w-full object-cover transition duration-700 group-hover:scale-105" data-testid={`event-image-${g.key}`} />
                  <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-ink via-ink/30 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <Icon className="h-5 w-5 text-cyan-400" />
                    <h3 className="mt-3 font-display text-xl font-bold text-white" data-testid={`event-title-${g.key}`}>{g.title}</h3>
                    <p className="mt-1 text-sm text-slate-300">{g.cap}</p>
                  </div>
                </div>
              </FadeUp>
            );
          })}
        </div>
      </section>

      <section className="border-y border-white/5 bg-[#0a0e18]" data-testid="events-specs">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-12 gap-y-4 px-4 py-10 sm:px-6 lg:px-12">
          {specs.map((s) => (
            <span key={s} className="font-mono text-xs uppercase tracking-[0.3em] text-slate-500" data-testid={`events-spec-${s.toLowerCase()}`}>
              {s}
            </span>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 text-center sm:px-6 lg:px-12 lg:py-28" data-testid="events-cta">
        <FadeUp>
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">We will make sure you're heard and seen.</h2>
          <p className="mx-auto mt-5 max-w-lg text-slate-400">Tell us the date and the dream — we coordinate everything else.</p>
          <Link to="/contact" data-testid="events-cta-button" className="mt-9 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-8 py-4 text-sm font-semibold text-ink transition hover:bg-cyan-300">
            Start planning <ArrowRight className="h-4 w-4" />
          </Link>
        </FadeUp>
      </section>
    </div>
  );
}
